import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Exam, ExamQuestion, ExamGradingResult, ExamAttemptAnswer } from "@/types/exams";
import { X, Loader2, ChevronDown, Share2, BookCheck, RotateCcw, Repeat, AlignJustify, ChevronRight } from "lucide-react";

import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { ExamShareDialog } from "@/components/exams/ExamShareDialog";

interface SubtopicResult {
  id: string;
  title: string;
  pageRange: string;
  startPage: number | null;
  correct: number;
  total: number;
  contentId: string | null;
}

interface TopicResult {
  id: string;
  title: string;
  contentId: string;
  correct: number;
  total: number;
  subtopics: SubtopicResult[];
}

export const RoomExamSummaryPage: React.FC = () => {
  usePageMeta("Exam Summary — Shattara AI", "", { noIndex: true });
  const { roomId, examId } = useParams();
  const navigate = useNavigate();

  const [exam, setExam] = useState<Exam | null>(null);
  const [examConfig, setExamConfig] = useState<{
    content_ids: string[] | null;
    question_count: number | null;
    question_types: string[] | null;
    duration_minutes: number | null;
  } | null>(null);
  const [allExams, setAllExams] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingRetake, setIsCreatingRetake] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [latestAttempt, setLatestAttempt] = useState<{
    id: string;
    score: number | null;
    total_correct: number | null;
    total_questions: number;
    duration_seconds: number | null;
    submitted_at: string | null;
    skipped_count: number;
  } | null>(null);
  const [topicResults, setTopicResults] = useState<TopicResult[]>([]);
  const [primaryContentTitle, setPrimaryContentTitle] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!examId) return;
      setLoading(true);

      try {
        // Fetch exam
        const { data: examRow, error: examError } = await supabase
          .from("exams")
          .select("*")
          .eq("id", examId)
          .maybeSingle();

        if (examError || !examRow) {
          console.error("Error fetching exam:", examError);
          toast.error("Failed to load exam");
          navigate(roomId ? `/room/${roomId}` : "/dashboard");
          return;
        }

        // Fetch questions
        const { data: questionRows } = await supabase
          .from("exam_questions")
          .select("*")
          .eq("exam_id", examId)
          .order("order_index", { ascending: true });

        const questions: ExamQuestion[] = (questionRows || []).map((q: any) => ({
          id: q.id,
          type: q.type,
          content: q.content,
          order_index: q.order_index,
        }));

        setExam({
          id: examRow.id,
          room_id: examRow.room_id,
          title: examRow.title ?? "Exam",
          status: examRow.status as Exam['status'],
          duration_minutes: examRow.duration_minutes ?? null,
          question_count: questions.length,
          questions,
        });

        // Store exam config for retake functionality
        setExamConfig({
          content_ids: examRow.content_ids,
          question_count: examRow.question_count,
          question_types: examRow.question_types,
          duration_minutes: examRow.duration_minutes,
        });

        // Fetch all exams in this room for dropdown
        if (roomId) {
          const { data: roomExams } = await supabase
            .from("exams")
            .select("id, title")
            .eq("room_id", roomId)
            .eq("status", "ready")
            .order("created_at", { ascending: false });

          if (roomExams) {
            setAllExams(roomExams.map((e, idx) => ({
              id: e.id,
              title: e.title ?? `Exam ${roomExams.length - idx}`,
            })));
          }
        }

        // Fetch latest attempt for current user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: attemptData } = await supabase
            .from("exam_attempts")
            .select("id, score, total_correct, total_questions, duration_seconds, submitted_at, status")
            .eq("exam_id", examId)
            .eq("user_id", user.id)
            .in("status", ["graded", "completed"])
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (attemptData) {
            // Fetch answers with question details to count skipped and build topic results
            const { data: answersData } = await supabase
              .from("exam_attempt_answers")
              .select("user_answer, is_correct, question_id")
              .eq("attempt_id", attemptData.id);

            const skippedCount = (answersData || []).filter(
              (a) => a.user_answer === null || a.user_answer === undefined || 
                     (typeof a.user_answer === 'string' && a.user_answer.trim() === '') ||
                     (typeof a.user_answer === 'string' && a.user_answer === '__SKIPPED__') ||
                     (Array.isArray(a.user_answer) && a.user_answer.length === 0)
            ).length;

            setLatestAttempt({
              ...attemptData,
              skipped_count: skippedCount,
            });

            // Build topic results from content and chapters
            if (examRow.content_ids && examRow.content_ids.length > 0 && answersData) {
              // Fetch content titles
              const { data: contentData } = await supabase
                .from("content")
                .select("id, title")
                .in("id", examRow.content_ids);

              // Fetch document chapters for all content
              const { data: chaptersData } = await supabase
                .from("document_chapters")
                .select("id, content_id, title, start_page, end_page, order_index")
                .in("content_id", examRow.content_ids)
                .order("order_index", { ascending: true });

              const contentMap = new Map<string, string>();
              (contentData || []).forEach(c => contentMap.set(c.id, c.title));

              // Set primary content title (first content)
              if (contentData && contentData.length > 0 && examRow.content_ids?.[0]) {
                const firstContentTitle = contentMap.get(examRow.content_ids[0]);
                if (firstContentTitle) {
                  setPrimaryContentTitle(firstContentTitle);
                }
              }

              // Group chapters by content_id
              const chaptersByContent = new Map<string, typeof chaptersData>();
              (chaptersData || []).forEach(ch => {
                if (!chaptersByContent.has(ch.content_id)) {
                  chaptersByContent.set(ch.content_id, []);
                }
                chaptersByContent.get(ch.content_id)!.push(ch);
              });

              // Build topic results - one per content
              const topicMap = new Map<string, {
                contentId: string;
                title: string;
                correct: number;
                total: number;
                subtopics: Map<string, SubtopicResult>;
              }>();

              questions.forEach((q) => {
                const content = q.content as any;
                const answer = answersData.find(a => a.question_id === q.id);
                const isCorrect = answer?.is_correct === true;

                // Try to match question to content and chapter
                const questionContentId = content?.content_id || examRow.content_ids?.[0] || '';
                
                // Parse page number from source_reference (e.g., "Page 3" -> 3)
                let questionPage: number | null = null;
                if (content?.source_reference) {
                  const pageMatch = content.source_reference.match(/page\s*(\d+)/i);
                  if (pageMatch) {
                    questionPage = parseInt(pageMatch[1], 10);
                  }
                }
                // Fallback to other page fields
                if (!questionPage) {
                  questionPage = content?.page || content?.start_page || null;
                }
                
                const contentTitle = contentMap.get(questionContentId) || 'General';
                
                if (!topicMap.has(questionContentId)) {
                  topicMap.set(questionContentId, {
                    contentId: questionContentId,
                    title: contentTitle,
                    correct: 0,
                    total: 0,
                    subtopics: new Map(),
                  });
                }

                const topicData = topicMap.get(questionContentId)!;
                topicData.total += 1;
                if (isCorrect) topicData.correct += 1;

                // Find matching chapter based on page number
                const chapters = chaptersByContent.get(questionContentId) || [];
                let matchedChapter = chapters.find(ch => 
                  questionPage && ch.start_page <= questionPage && ch.end_page >= questionPage
                );
                
                // If no page match, use topic/chapter from question content
                const chapterTitle = matchedChapter?.title || content?.chapter || content?.topic || 'General';
                const startPage = matchedChapter?.start_page || questionPage || null;
                const endPage = matchedChapter?.end_page || questionPage || null;
                const pageRange = startPage && endPage 
                  ? startPage === endPage 
                    ? `Page ${startPage}` 
                    : `Page ${startPage} - ${endPage}`
                  : '';

                const subtopicKey = `${chapterTitle}-${pageRange}`;
                if (!topicData.subtopics.has(subtopicKey)) {
                  topicData.subtopics.set(subtopicKey, {
                    id: `subtopic-${questionContentId}-${topicData.subtopics.size}`,
                    title: chapterTitle,
                    pageRange: pageRange,
                    startPage: startPage,
                    correct: 0,
                    total: 0,
                    contentId: questionContentId,
                  });
                }

                const subtopicData = topicData.subtopics.get(subtopicKey)!;
                subtopicData.total += 1;
                if (isCorrect) subtopicData.correct += 1;
              });

              const results: TopicResult[] = Array.from(topicMap.entries()).map(([key, value], idx) => ({
                id: `topic-${idx}`,
                title: value.title,
                contentId: value.contentId,
                correct: value.correct,
                total: value.total,
                subtopics: Array.from(value.subtopics.values()),
              }));

              setTopicResults(results);
            } else if (questions.length > 0 && answersData) {
              // Fallback: group by topic/chapter from question content (no content_ids)
              const topicMap = new Map<string, {
                contentId: string;
                title: string;
                correct: number;
                total: number;
                subtopics: Map<string, SubtopicResult>;
              }>();

              questions.forEach((q, idx) => {
                const content = q.content as any;
                const topic = content?.topic || content?.chapter || 'General';
                const subtopic = content?.subtopic || content?.section || topic;
                const pageRange = content?.page_range || content?.pages || '';
                
                const answer = answersData.find(a => a.question_id === q.id);
                const isCorrect = answer?.is_correct === true;

                if (!topicMap.has(topic)) {
                  topicMap.set(topic, {
                    contentId: '',
                    title: topic,
                    correct: 0,
                    total: 0,
                    subtopics: new Map(),
                  });
                }

                const topicData = topicMap.get(topic)!;
                topicData.total += 1;
                if (isCorrect) topicData.correct += 1;

                const subtopicKey = `${subtopic}-${pageRange}`;
                if (!topicData.subtopics.has(subtopicKey)) {
                  topicData.subtopics.set(subtopicKey, {
                    id: `subtopic-${idx}-${topicData.subtopics.size}`,
                    title: subtopic,
                    pageRange: pageRange,
                    startPage: null,
                    correct: 0,
                    total: 0,
                    contentId: null,
                  });
                }

                const subtopicData = topicData.subtopics.get(subtopicKey)!;
                subtopicData.total += 1;
                if (isCorrect) subtopicData.correct += 1;
              });

              const results: TopicResult[] = Array.from(topicMap.entries()).map(([key, value], idx) => ({
                id: `topic-${idx}`,
                title: value.title,
                contentId: value.contentId,
                correct: value.correct,
                total: value.total,
                subtopics: Array.from(value.subtopics.values()),
              }));

              setTopicResults(results);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [examId, roomId, navigate]);

  const handleClose = () => {
    if (roomId) {
      navigate(`/room/${roomId}`);
    } else {
      navigate("/dashboard");
    }
  };

  const handleBackToResults = () => {
    navigate(`/room/${roomId}/exam/${examId}?mode=results`);
  };

  const handleExamSelect = (selectedExamId: string) => {
    if (selectedExamId !== examId) {
      navigate(`/room/${roomId}/exam/${selectedExamId}/summary`);
    }
  };

  const handleTryAgain = () => {
    // Navigate to the exam page to restart with same questions
    navigate(`/room/${roomId}/exam/${examId}`);
  };

  const handleReviewChapter = (contentId: string | null, startPage: number | null) => {
    if (!contentId) return;
    // Navigate to content page with optional page parameter
    const pageParam = startPage ? `?page=${startPage}` : '';
    navigate(`/content/${contentId}${pageParam}`);
  };

  const handleNewQuestionRetake = async () => {
    if (!examConfig || !roomId) {
      toast.error("Unable to create retake exam");
      return;
    }

    setIsCreatingRetake(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please sign in to create an exam");
        return;
      }

      // Create a new exam with the same configuration
      const { data: newExam, error } = await supabase
        .from("exams")
        .insert({
          user_id: user.id,
          room_id: roomId,
          content_ids: examConfig.content_ids,
          question_count: examConfig.question_count,
          question_types: examConfig.question_types,
          duration_minutes: examConfig.duration_minutes,
          title: `${exam?.title || 'Exam'} (Retake)`,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // Call edge function to generate new questions
      const { error: genError } = await supabase.functions.invoke("generate-exam", {
        body: { exam_id: newExam.id }
      });

      if (genError) {
        console.error("Error generating exam:", genError);
      }

      toast.success("Creating new exam with fresh questions...");
      
      // Navigate to the new exam
      navigate(`/room/${roomId}/exam/${newExam.id}`);
    } catch (error) {
      console.error("Error creating retake exam:", error);
      toast.error("Failed to create new exam");
    } finally {
      setIsCreatingRetake(false);
    }
  };

  const handleShare = () => {
    setShareDialogOpen(true);
  };

  const currentExamTitle = allExams.find(e => e.id === examId)?.title || exam?.title || "Exam";
  const currentExamIndex = allExams.findIndex(e => e.id === examId);
  const examNumber = currentExamIndex >= 0 ? allExams.length - currentExamIndex : 1;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading summary...</p>
        </div>
      </div>
    );
  }

  const scorePercent = latestAttempt?.score ?? 0;
  const totalCorrect = latestAttempt?.total_correct ?? 0;
  const totalQuestions = latestAttempt?.total_questions ?? exam?.question_count ?? 0;

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const skippedCount = latestAttempt?.skipped_count ?? 0;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 w-full border-b bg-background/95 px-4 shadow-lg shadow-neutral-800/5 backdrop-blur-sm dark:border-border dark:shadow-white/5">
        <div className="relative mx-auto flex w-full items-center py-4">
          {/* Left: close */}
          <div className="flex w-20 flex-shrink-0 items-center gap-2 xl:w-40">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Center: Exam Dropdown */}
          <div className="flex flex-1 items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="text-base font-medium text-neutral-600 dark:text-neutral-300">
                    Exam {examNumber}
                  </span>
                  <ChevronDown className="h-4 w-4 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="min-w-[160px]">
                {allExams.map((e, idx) => (
                  <DropdownMenuItem
                    key={e.id}
                    onClick={() => handleExamSelect(e.id)}
                    className={e.id === examId ? "bg-accent" : ""}
                  >
                    Exam {allExams.length - idx}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right: Share Button */}
          <div className="flex w-20 flex-shrink-0 items-center justify-end gap-2 xl:w-40">
            <button
              type="button"
              onClick={handleShare}
              className="relative inline-flex h-10 items-center justify-center gap-x-2 whitespace-nowrap rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-primary/10"
            >
              <Share2 className="inline h-4 w-4 scale-x-[-1] xl:hidden" aria-hidden="true" />
              <span className="hidden xl:inline">Share exam</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-grow overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          {/* Score Card */}
          <div className="mb-8 text-center">
            <h2 className="my-10 text-xl font-medium text-primary/80 dark:text-primary sm:my-12 sm:text-2xl">
              {scorePercent >= 90
                ? "Excellent work! You've mastered this material."
                : scorePercent >= 70
                ? "You're doing well, keep it up!"
                : scorePercent >= 50
                ? "You're making progress. Keep going!"
                : "Keep practicing! You'll get there."}
            </h2>
            
            <div className="mb-8 flex items-center justify-center gap-x-12 sm:gap-x-24">
              {/* Skipped */}
              <div className="text-center">
                <div className="text-xl font-medium sm:text-2xl">{skippedCount}</div>
                <div className="text-xs text-muted-foreground/80 dark:text-muted-foreground/80 sm:text-sm">Skipped</div>
              </div>
              
              {/* Circular Progress */}
              <div className="relative flex h-24 w-24 flex-col items-center justify-center sm:h-32 sm:w-32">
                <svg className="absolute left-0 top-0 h-full w-full" viewBox="0 0 100 100">
                  <circle
                    className="stroke-current text-primary/5 dark:text-primary/10"
                    strokeWidth="10"
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                  />
                  <circle
                    className="stroke-current text-warning"
                    strokeWidth="10"
                    strokeLinecap="butt"
                    cx="50"
                    cy="50"
                    r="45"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 45}
                    strokeDashoffset={2 * Math.PI * 45 * (1 - scorePercent / 100)}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <span className="z-10 text-xl font-bold text-neutral-700 dark:text-primary sm:text-2xl">
                  {scorePercent.toFixed(1)}%
                </span>
                <span className="z-10 text-xs text-muted-foreground dark:text-muted-foreground sm:text-sm">Score</span>
              </div>
              
              {/* Time Taken */}
              <div className="text-center">
                <div className="text-xl font-medium sm:text-2xl">{formatDuration(latestAttempt?.duration_seconds ?? null)}</div>
                <div className="text-xs text-muted-foreground/80 dark:text-muted-foreground/80 sm:text-sm">Time Taken</div>
              </div>
            </div>
          </div>

          {/* Preview Exam Link */}
          <div className="mb-8 flex justify-center">
            <button
              type="button"
              onClick={handleBackToResults}
              className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold text-primary/60 underline underline-offset-4 ring-offset-background transition-colors hover:text-primary hover:underline focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:text-primary/60 dark:hover:text-primary"
            >
              Preview {primaryContentTitle || currentExamTitle} - Exam {examNumber}
            </button>
          </div>

          {/* Topic Results Accordion */}
          <div className="mb-8 flex flex-col gap-6 rounded-2xl border p-6 pb-2 dark:bg-neutral-950/20">
              <Accordion type="multiple" className="flex flex-col gap-6">
                {topicResults.map((topic) => {
                  const progressPercent = topic.total > 0 ? (topic.correct / topic.total) * 100 : 0;
                  return (
                    <AccordionItem key={topic.id} value={topic.id} className="border-b last:mb-0 last:border-b-0">
                      <AccordionTrigger className="group mb-4 flex flex-1 cursor-pointer items-center justify-between p-0 font-medium transition-all hover:no-underline [&>svg]:hidden">
                        <h3 className="flex items-center gap-2 text-base font-medium">
                          <AlignJustify 
                            className="block h-4 w-4 flex-shrink-0 text-neutral-800 group-hover:hidden dark:text-neutral-200" 
                            aria-hidden="true" 
                          />
                          <ChevronDown 
                            className="hidden h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform duration-200 group-hover:block group-data-[state=open]:rotate-180 dark:text-neutral-500" 
                            aria-hidden="true" 
                          />
                          <span className="max-w-[130px] truncate text-sm sm:max-w-[250px] sm:text-base md:max-w-[300px] lg:max-w-[400px]">
                            {topic.title}
                          </span>
                        </h3>
                        <div className="flex items-center gap-4">
                          <Progress 
                            value={progressPercent} 
                            className="h-3.5 w-16 bg-primary/5 dark:bg-primary/10 sm:w-24 md:w-32 [&>div]:bg-warning"
                          />
                          <span className="text-sm text-neutral-600 dark:text-neutral-400">
                            {topic.correct}/{topic.total}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="overflow-hidden text-sm">
                        <div className="pb-4 pt-0 flex flex-col gap-4 space-y-1 duration-300 animate-in fade-in sm:pl-3">
                          {topic.subtopics.map((subtopic) => {
                            const isComplete = subtopic.correct === subtopic.total && subtopic.total > 0;
                            return (
                              <div key={subtopic.id} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                                  <span className="max-w-[160px] truncate text-xs sm:max-w-[260px] sm:text-sm md:max-w-[280px] lg:max-w-[500px]">
                                    {subtopic.title}
                                  </span>
                                  {subtopic.pageRange && (
                                    <>
                                      <span className="hidden text-xs text-primary sm:text-sm md:block">•</span>
                                      <span className="hidden text-xs text-muted-foreground/80 sm:text-sm md:block">
                                        {subtopic.pageRange}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400 sm:text-sm">
                                  {isComplete ? (
                                    <button 
                                      type="button"
                                      className="justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 flex h-6 cursor-default items-center gap-2 rounded-md bg-correct/10 px-2 py-1 text-xs font-normal text-correct hover:bg-correct/20 hover:text-primary/80"
                                    >
                                      <span>Complete</span>
                                    </button>
                                  ) : (
                                    <button 
                                      type="button"
                                      onClick={() => handleReviewChapter(subtopic.contentId, subtopic.startPage)}
                                      className="justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 flex h-6 cursor-pointer items-center gap-1 rounded-md border border-warning/20 bg-warning/10 px-2 py-1 text-xs font-normal text-warning hover:bg-warning/20 hover:text-warning/80 dark:bg-warning/30 dark:text-warning"
                                    >
                                      Review
                                      <ChevronRight className="h-3 w-3" aria-hidden="true" />
                                    </button>
                                  )}
                                  <span className="font-medium text-neutral-600 dark:text-neutral-400">
                                    {subtopic.correct}/{subtopic.total}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
          </div>

          {/* Action Buttons */}
          <div className="mb-24 flex flex-col justify-center gap-2 sm:flex-row sm:gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleTryAgain}
                    className="inline-flex h-11 items-center justify-center gap-x-2 whitespace-nowrap rounded-full border border-input bg-background px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Try Again
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Run the same exam—same questions, same order</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleNewQuestionRetake}
                    disabled={isCreatingRetake}
                    className="inline-flex h-11 items-center justify-center gap-x-2 whitespace-nowrap rounded-full border border-input bg-background px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                  >
                    {isCreatingRetake ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Repeat className="h-4 w-4" aria-hidden="true" />
                    )}
                    New-Question Retake
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Generate a fresh set of questions on the same contents—perfect for deeper practice</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </main>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 border-t-[1.5px] border-border bg-background px-4 py-8 md:py-[clamp(1rem,2.5vh,2.5rem)]">
        <div className="mx-auto flex max-w-4xl justify-center gap-4">
          <div className="flex w-full justify-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-full border border-input bg-background px-8 text-base font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:w-96"
            >
              Go to Room
            </button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => navigate(`/room/${roomId}?mode=exam`)}
                    className="inline-flex h-12 w-full items-center justify-center gap-x-2 whitespace-nowrap rounded-full bg-primary px-8 text-base font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:w-96"
                  >
                    <BookCheck className="h-4 w-4" aria-hidden="true" />
                    Create Exam
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Pick new contents to create an all new exam</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <ExamShareDialog
        examId={examId ?? null}
        examTitle={currentExamTitle}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
    </div>
  );
};
